import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCourses, deleteCourse, publishCourse } from '../../services/api';
import { useAuth } from '../../context/AuthContext.jsx';
import { FaPlus, FaBook, FaTrash, FaEye, FaEdit } from 'react-icons/fa';
import ConfirmModal from '../../components/common/ConfirmModal.jsx';
import { toast } from 'react-toastify';
import './Instructor.css';

const InstructorDashboard = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, course: null });

  const fetchMyCourses = useCallback(async () => {
    try {
      const res = await getCourses({ instructor: user.id });
      setCourses(res.data.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchMyCourses();
  }, [fetchMyCourses]);

  const handleDeleteClick = (course) => {
    setDeleteModal({ isOpen: true, course });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCourse(deleteModal.course._id);
      toast.success(`${deleteModal.course.courseType === 'ebook' ? 'Ebook' : 'Course'} deleted successfully`);
      setDeleteModal({ isOpen: false, course: null });
      fetchMyCourses(); // Refresh the course list
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(error.response?.data?.message || 'Failed to delete course');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, course: null });
  };

  const handleTogglePublish = async (course) => {
    const newPublishedState = !course.published;
    try {
      await publishCourse(course._id, newPublishedState);
      toast.success(`${course.courseType === 'ebook' ? 'Ebook' : 'Course'} ${newPublishedState ? 'published' : 'unpublished'} successfully`);
      fetchMyCourses(); // Refresh the course list
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  return (
    <div className="instructor-dashboard">
      <div className="page-header">
        <div className="container">
          <div className="header-content">
            <div>
              <h1>Instructor Dashboard</h1>
              <p>Manage your courses and students</p>
            </div>
            <Link to="/instructor/courses/create" className="btn btn-primary">
              <FaPlus /> Create Course
            </Link>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="page-content">
          <h2>My Courses</h2>
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : courses.length === 0 ? (
            <div className="empty-state">
              <FaBook size={64} />
              <h3>No courses yet</h3>
              <p>Create your first course to start teaching</p>
              <Link to="/instructor/courses/create" className="btn btn-primary">
                <FaPlus /> Create Course
              </Link>
            </div>
          ) : (
            <div className="courses-grid">
              {courses.map((course) => (
                <div key={course._id} className="course-card">
                  <div className="course-thumbnail">
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.title} />
                    ) : (
                      <div className="placeholder-thumbnail">
                        <FaBook size={48} />
                      </div>
                    )}
                    <span className={`badge ${course.published ? 'badge-success' : 'badge-warning'}`}>
                      {course.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div className="course-info">
                    <h3>{course.title}</h3>
                    <div className="course-stats">
                      <span>{course.enrollmentCount} students</span>
                      <span>{course.totalLessons} lessons</span>
                      <span className="views-count">
                        <FaEye /> {course.views || 0} views
                      </span>
                    </div>
                    <div className="course-meta">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={course.published}
                          onChange={() => handleTogglePublish(course)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">
                        {course.published ? 'Published' : 'Unpublished'}
                      </span>
                    </div>
                    <div className="course-actions">
                      <Link to={`/instructor/courses/${course._id}/edit`} className="btn btn-primary-outline">
                        <FaEdit /> Edit
                      </Link>
                      <button
                        className="btn btn-danger-outline"
                        onClick={() => handleDeleteClick(course)}
                        title="Delete course"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${deleteModal.course?.courseType === 'ebook' ? 'Ebook' : 'Course'}`}
        message={`Are you sure you want to delete "${deleteModal.course?.title}"? This action cannot be undone and will remove all associated content, files, and enrollments.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default InstructorDashboard;
